import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, CheckCircle, Clock, Building, Landmark, ChevronRight, ShieldCheck, FileCheck, FileSignature, Users, Archive } from 'lucide-react';

import escudoImg from '../assets/CBR_Logo_Icono.png';
import fuenteImg from '../assets/pileta_soleada.png';
import HomenajeModal from '../components/HomenajeModal';

export default function BuscadorPublico() {
  const [form, setForm] = useState({ apellido_paterno: '', apellido_materno: '', primer_nombre: '' });

  const navigate = useNavigate();

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validateForm = () => {
    if (!form.apellido_paterno && !form.apellido_materno && !form.primer_nombre) {
      toast.error('Ingrese al menos un criterio de búsqueda.');
      return false;
    }
    return true;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const params = new URLSearchParams();
    if (form.apellido_paterno) params.append('apellido_paterno', form.apellido_paterno);
    if (form.apellido_materno) params.append('apellido_materno', form.apellido_materno);
    if (form.primer_nombre) params.append('primer_nombre', form.primer_nombre);

    navigate(`/indicepropiedad?${params.toString()}`);
  };

  return (
    <div className="page-wrapper" style={{ background: '#f8fafc' }}>
      <HomenajeModal />

      {/* ── HERO SECTION ── */}
      <section style={{ position: 'relative', padding: '6rem 0 8rem', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#f8fafc' }}>

        {/* Fountain Background with Parallax and Vignette */}
        <div className="animate-fade-in" style={{
          position: 'absolute',
          inset: '-20px',
          zIndex: 0,
          backgroundImage: `linear-gradient(to bottom, rgba(248,250,252,0.6) 0%, rgba(248,250,252,0.1) 50%, rgba(248,250,252,0.6) 100%), url(${fuenteImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          opacity: 0.12,
          filter: 'grayscale(20%) blur(10px) contrast(105%)',
          WebkitAnimationDuration: '1.5s'
        }} />


        <div className="hero-mesh-bg" style={{ zIndex: 1, backgroundColor: 'transparent' }} />

        <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>


          <img
            src={import.meta.env.VITE_THEME_LOGO || escudoImg}
            alt={`Escudo ${import.meta.env.VITE_CBR_NAME || "CBR San Carlos"}`}
            className="floating"
            style={{ width: '180px', height: '180px', filter: 'drop-shadow(0 15px 25px rgba(0,51,102,0.15))', marginBottom: '2rem', objectFit: 'contain' }}
          />
          <h1 className="animate-fade-in" style={{
            fontSize: 'clamp(3rem, 5vw, 4.5rem)',
            fontWeight: 800,
            color: 'var(--primary-dark)',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            marginBottom: '1rem',
            maxWidth: '900px',
            textShadow: '0 2px 20px rgba(255,255,255,0.8), 0 0 40px rgba(255,255,255,0.4)',
          }}>
            Conservador de Bienes Raíces <span style={{
              color: 'var(--accent)',
              WebkitTextStroke: '1px var(--primary-dark)',
              textStroke: '1px',
              fontWeight: 800,
              display: 'inline-block',
            }}>{import.meta.env.VITE_CBR_CITY || "San Carlos"}</span>
          </h1>

          <p className="animate-fade-in" style={{
            fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
            color: 'var(--primary-dark)',

            fontWeight: 500,
            textShadow: '0 2px 10px rgba(255,255,255,0.8), 0 0 20px rgba(255,255,255,0.6)',
            maxWidth: '700px',
            margin: '0 auto 3.5rem',
            lineHeight: 1.6,
            WebkitAnimationDelay: '0.1s',
            animationFillMode: 'both'
          }}>
            Repositorio Digital Oficial de la República. Consulte inscripciones, valide certificados y solicite documentos.
          </p>

          {/* Pill Search Form */}
          <form className="hero-pill-search animate-fade-in" style={{ WebkitAnimationDelay: '0.2s', animationFillMode: 'both' }} onSubmit={handleSearch}>
            <div style={{ display: 'flex', flex: 1, alignItems: 'center', padding: '0 10px' }}>
              <Search size={20} color="var(--text-muted)" style={{ flexShrink: 0 }} />
              <input type="text" name="apellido_paterno" value={form.apellido_paterno} onChange={handleChange} placeholder="Apellido Paterno / Razón Social" className="pill-input" />
            </div>
            <div className="pill-divider" />

            <div style={{ display: 'flex', flex: 1, alignItems: 'center', padding: '0 10px' }}>
              <input type="text" name="apellido_materno" value={form.apellido_materno} onChange={handleChange} placeholder="Apellido Materno" className="pill-input" />
            </div>
            <div className="pill-divider" />

            <div style={{ display: 'flex', flex: 1, alignItems: 'center', padding: '0 10px' }}>
              <input type="text" name="primer_nombre" value={form.primer_nombre} onChange={handleChange} placeholder="Primer Nombre" className="pill-input" />
            </div>

            <button type="submit" className="pill-btn">
              <Search size={20} />
              <span>Buscar</span>
            </button>
          </form>

        </div>
      </section>

      {/* ── BENTO GRID SECTION ── */}
      <section style={{ padding: '0 0 6rem', position: 'relative', zIndex: 2, marginTop: '-4rem' }}>
        <div className="container" style={{ padding: '0 clamp(16px, 4vw, 48px)' }}>
          <div className="bento-grid">

            {/* Bento 1: Solicitar Certificados */}
            <div className="bento-item bento-col-4 animate-fade-in" style={{ WebkitAnimationDelay: '0.3s', animationFillMode: 'both' }}>
              <div style={{ display: 'inline-flex', padding: '12px', background: 'rgba(0,51,102,0.06)', borderRadius: '16px', color: 'var(--primary)', marginBottom: '1.5rem' }}>
                <FileCheck size={28} />
              </div>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '0.8rem', color: 'var(--primary-dark)' }}>Solicitar Certificados</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '2rem', flex: 1 }}>
                Solicite Copias Autorizadas, Dominios Vigentes, Certificados de Hipotecas y Gravámenes con envío directo a su correo electrónico.
              </p>
              <button onClick={() => navigate('/solicitudes')} className="btn btn-primary" style={{ width: '100%', borderRadius: '12px' }}>
                Solicitar Certificados <ChevronRight size={18} />
              </button>
            </div>

            {/* Bento 2: Ingreso de Trámites */}
            <div className="bento-item bento-col-4 animate-fade-in" style={{ WebkitAnimationDelay: '0.4s', animationFillMode: 'both', background: 'linear-gradient(145deg, rgba(255,255,255,0.8), rgba(248,250,252,0.9))' }}>
              <div style={{ display: 'inline-flex', padding: '12px', background: 'rgba(5,150,105,0.08)', borderRadius: '16px', color: 'var(--success)', marginBottom: '1.5rem' }}>
                <FileSignature size={28} />
              </div>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '0.8rem', color: 'var(--primary-dark)' }}>Ingreso Trámite</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '2rem', flex: 1 }}>
                Inicie solicitudes de inscripción, repertorios y otros trámites registrales de manera remota y segura.
              </p>
              <button onClick={() => navigate('/ingresotramite')} className="btn btn-primary" style={{ width: '100%', borderRadius: '12px' }}>
                Ingresar Trámite <ChevronRight size={18} />
              </button>
            </div>

            {/* Bento 3: Quiénes Somos */}
            <div className="bento-item bento-col-4 animate-fade-in" style={{ WebkitAnimationDelay: '0.5s', animationFillMode: 'both' }}>
              <div style={{ display: 'inline-flex', padding: '12px', background: 'rgba(212,175,55,0.1)', borderRadius: '16px', color: 'var(--warning)', marginBottom: '1.5rem' }}>
                <Users size={28} />
              </div>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '0.8rem', color: 'var(--primary-dark)' }}>Quiénes Somos</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '2rem', flex: 1 }}>
                Conozca la historia, nuestra misión y el equipo de profesionales detrás del Conservador de Bienes Raíces de {import.meta.env.VITE_CBR_CITY || "San Carlos"}.
              </p>
              <button onClick={() => navigate('/quienes-somos')} className="btn btn-secondary" style={{ width: '100%', borderRadius: '12px' }}>
                Conocer Más
              </button>
            </div>

            {/* Bento 3.5: Archivero Judicial (Oculto temporalmente) 
            <div className="bento-item bento-col-8 animate-fade-in" style={{ WebkitAnimationDelay: '0.55s', animationFillMode: 'both', background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(245,247,250,1))' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: 1 }}>
                <div>
                  <div style={{ display: 'inline-flex', padding: '12px', background: 'rgba(100,116,139,0.1)', borderRadius: '16px', color: '#475569', marginBottom: '1.5rem' }}>
                    <Archive size={32} />
                  </div>
                  <h2 style={{ fontSize: '2rem', marginBottom: '0.8rem', color: 'var(--primary-dark)' }}>Archivero Judicial</h2>
                  <p style={{ color: 'var(--text-muted)', fontSize: '1.05rem', lineHeight: 1.6, maxWidth: '85%' }}>
                    Solicite copias de escrituras públicas, expedientes y documentos almacenados en el Archivo Judicial de San Carlos.
                  </p>
                </div>
              </div>
              <div style={{ marginTop: '2rem' }}>
                <button onClick={() => navigate('/archivero')} className="btn btn-primary" style={{ padding: '1rem 2rem', fontSize: '1.1rem', borderRadius: '12px', background: '#475569', borderColor: '#475569' }}>
                  Ir al Archivero <ChevronRight size={18} />
                </button>
              </div>
            </div>
            */}

            {/* Bento 4: Información Bancaria e Institucional (Wallet Card) */}
            <div className="bento-col-12 animate-fade-in" style={{ WebkitAnimationDelay: '0.6s', animationFillMode: 'both' }}>
              <div className="wallet-card">
                <div>
                  <div className="wallet-chip" />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <h4 style={{ color: 'var(--accent-light)', textTransform: 'uppercase', letterSpacing: '2px', fontSize: '0.8rem', marginBottom: '4px' }}>Información Bancaria</h4>
                      <div style={{ fontSize: '1.6rem', fontFamily: 'monospace', letterSpacing: '2px', marginBottom: '8px', color: '#fff' }}>
                        {import.meta.env.VITE_BANK_ACCOUNT_NUM || "56998023"}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                        Tipo de cuenta: {import.meta.env.VITE_BANK_ACCOUNT_TYPE || "Cuenta Corriente"}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                        {import.meta.env.VITE_BANK_NAME || "Banco BCI"}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                        Nombre: {import.meta.env.VITE_BANK_HOLDER_NAME || "Raúl Hernán Fuentes Jiménez"}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                        RUT: {import.meta.env.VITE_BANK_HOLDER_RUT || "13.793.838-3"}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Landmark size={48} color="rgba(255,255,255,0.15)" strokeWidth={1} style={{ marginBottom: '8px' }} />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '2.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--accent-light)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Horario de Atención</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {import.meta.env.VITE_OFFICE_HOURS || "Horario de atencion: Lunes a Viernes: 09:00 a 16:00"}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--accent-light)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Contacto Pagos</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {import.meta.env.VITE_CONTACT_EMAIL || "conservadorsancarlos@hotmail.com"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* La sección de tabla de resultados fue removida para redirigirse a otra vista */}

    </div>
  );
}
